// @givmo/compliance — one item of a prepared form, on the workbench (CCM-R1-DOCS; slice 2 §5.2
// and its words, frames 31–37).
//
// Every row reads the item's SERVED state; nothing here works out whether an item is answered,
// applies or fits. The server says it, and the row shows it in the approved words:
//
//   from a record    the value, which record it came from, and Change;
//   records differ   the two records as a choice, then "Use this";
//   answered         the value and who entered it (or chose it, or marked "Doesn't apply");
//   asked            the field(s) the item's kind takes, the form's own instruction under them,
//                    and the form's own answers where it names them ("Write “see attached”");
//   printed          "Printed on the form.";
//   not asked        not shown: the form asks it only after another answer (its `condition`).
//
// A person who may not change the form (a viewer, or anyone while it is out for signature) sees
// the values and their lines, and "Not answered yet" where the form still waits.
//
// The fee's field carries the form's own fee chart under it, in place of the instruction that
// points to it (frame 34; CCM-R1-DOCS-PRINTED-WEB): the item then stacks, as the frame draws it. A
// question the form prints over several items is drawn by `filing-workbench-group.tsx`.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Field,
  RadioTile,
  RadioTileGroup,
  Select,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import type { ComplianceFormAnswerIn } from '@givmo/api-client';
import { useCompliance } from './compliance-context';
import { serverMessage } from './compliance-decode';
import {
  decodeFormPreparation,
  type FormFeeChart,
  type FormItem,
  type FormPreparation,
  type FormSource,
} from './compliance-preparation-decode';
import {
  CANCEL,
  CHANGE,
  DOESNT_APPLY,
  NAME_AND_ADDRESS,
  NOT_ANSWERED_YET,
  PRINTED_ATTACHMENTS_CLAUSE,
  PRINTED_ON_THE_FORM,
  RECORDS_DIFFER,
  SAVE,
  USE_THIS,
  answeredLine,
  instructionLine,
  itemDisplayLabel,
  MONTH_YEAR_CAPTION,
  naLine,
  notRequiredLine,
  recordParagraph,
  sourceLine,
  wordCountLine,
  type SourceContext,
} from './compliance-preparation-model';
import { formPreparationQueryKey } from './compliance-queries';
import { WorkbenchFiscalYearEnd } from './filing-panel-forms';
import { FeeChart } from './filing-workbench-fee-chart';
import { QuietButton } from './filing-workbench-pages';

export const ITEM_PREFIX = 'compliance-workbench-item';
const PREFIX = ITEM_PREFIX;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** The SAVE_FAILED fallback, as the panel's forms say it. */
export const SAVE_FAILED = 'We couldn’t save that. Nothing was changed — please try again.';

/** One quiet line under a value: where it came from, or who entered it. */
export function SourceLine({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p className="text-s12 leading-h15 text-text-tertiary" {...testIdProp(testId)}>
      {children}
    </p>
  );
}

/** The form's own instruction, quoted under the item it asks. */
function Instruction({ item, jurisdiction }: { item: FormItem; jurisdiction: string }) {
  if (item.instruction === undefined) return null;
  return (
    <SourceLine testId={`${PREFIX}-${item.key}-instruction`}>
      {instructionLine(jurisdiction, item.instruction.text)}
    </SourceLine>
  );
}

/** How many columns a table item's rows have, read off its served places: a row shares a band. */
export function tableColumns(item: FormItem): number {
  const first = item.places[0];
  if (first === undefined) return 1;
  const columns = item.places.filter(
    (place) =>
      place.page === first.page &&
      place.rect[1] === first.rect[1] &&
      place.rect[3] === first.rect[3],
  ).length;
  return Math.max(1, columns);
}

/** The value as the form shows it, one string per line. */
export function valueLines(item: FormItem): string[] {
  if (item.kind === 'checked' || item.kind === 'yes_no_written') {
    const chosen = item.choices.find((choice) => choice.key === item.choice);
    if (chosen !== undefined) return [chosen.label];
  }
  if (item.kind === 'month_year') return [item.values.filter((v) => v.length > 0).join(' · ')];
  if (item.kind === 'table') {
    const columns = tableColumns(item);
    const rows: string[] = [];
    for (let index = 0; index < item.values.length; index += columns) {
      const row = item.values.slice(index, index + columns).filter((v) => v.length > 0);
      if (row.length > 0) rows.push(row.join(' · '));
    }
    return rows;
  }
  return item.values.filter((value) => value.length > 0);
}

/** "Changed by … on …. Your profile still says …" — an entry over a value the profile holds. */
function changedFromProfile(item: FormItem): FormSource | undefined {
  if (item.answer?.kind !== 'value') return undefined;
  const profile = item.sources.find(
    (source) => source.kind === 'organization_profile' && source.value !== undefined,
  );
  if (profile === undefined) return undefined;
  const written = valueLines(item).join(' ');
  return profile.value !== written ? profile : undefined;
}

/**
 * Whether the item's value is the organization's fiscal year end, which the profile holds. Read off
 * the records the form DECLARES for it (`reads`), so it holds before any is recorded: a form with
 * no fiscal year end on record asks for the organization's, never for a value of this filing's
 * alone (frame 34). A wire without `reads` falls back to the records that hold a value.
 */
function isFiscalYearEndItem(item: FormItem): boolean {
  const records = item.reads.length > 0 ? item.reads : item.sources;
  return records.some(
    (record) => record.kind === 'compliance_profile' || record.kind === 'filing_cycle',
  );
}

/** Whether a choice item is the form's own Yes and No, asked as one two-way control. */
export function isYesNo(item: FormItem): boolean {
  if (item.kind === 'yes_no_written') return true;
  if (item.kind !== 'checked' || item.choices.length !== 2) return false;
  const labels = item.choices.map((choice) => choice.label.trim().toLowerCase());
  return labels.includes('yes') && labels.includes('no');
}

export interface WorkbenchItemProps {
  item: FormItem;
  preparation: FormPreparation;
  jurisdiction: string;
  /** May this person change the form now (a writer, and the form not out for signature). */
  editable: boolean;
  selected: boolean;
  onSelect: () => void;
  context: SourceContext;
  personName?: (userId: string) => string | undefined;
  /** The other item the form's "Same as" copies, where it names one. */
  sameAsLabel?: string;
  /** The form's own fee chart, drawn under this item's field (the fee's; frame 34). */
  feeChart?: FormFeeChart;
}

/** One item, in the two columns: the form's words left, the value or the field right. */
export function WorkbenchItem({
  item,
  preparation,
  jurisdiction,
  editable,
  selected,
  onSelect,
  context,
  personName,
  sameAsLabel,
  feeChart,
}: WorkbenchItemProps) {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // The fiscal year end saved here fills the item from the record, and its question goes: focus
  // moves to the value it now reads, never to the page (CCM-R1-INTAKE-FYE-A11Y).
  const value = useRef<HTMLDivElement>(null);
  const [focusValue, setFocusValue] = useState(false);
  const answer = useMutation({
    mutationFn: (body: ComplianceFormAnswerIn) =>
      operations.answerFormItem(orgRef ?? '', preparation.preparationId, item.key, body),
    onSuccess: (body, sent) => {
      const next = decodeFormPreparation(body);
      if (next !== undefined) {
        queryClient.setQueryData(formPreparationQueryKey(orgRef, preparation.preparationId), body);
      } else {
        void queryClient.invalidateQueries({
          queryKey: formPreparationQueryKey(orgRef, preparation.preparationId),
        });
      }
      setRefusal(null);
      setEditing(false);
      if (sent.kind === 'doesnt_apply' && item.doesntApply?.writes === 'N/A') {
        toast.success(naLine(jurisdiction));
      }
    },
    // An answer the form cannot take is refused with the server's own line, under the field.
    onError: (cause) => setRefusal(serverMessage(cause, SAVE_FAILED)),
  });
  const send = (body: ComplianceFormAnswerIn) => answer.mutate(body);

  const label = (
    <p
      className="text-s13 leading-h15 text-text-secondary"
      {...testIdProp(`${PREFIX}-${item.key}-label`)}
    >
      {itemDisplayLabel(item)}
      {item.kind === 'month_year' && (
        <>
          <br />
          {MONTH_YEAR_CAPTION}
        </>
      )}
    </p>
  );
  const lines = valueLines(item);
  const asked = item.state === 'needs_input' || item.state === 'optional_empty';
  const troubled = item.state === 'not_printable' || item.state === 'does_not_fit';
  useEffect(() => {
    if (!focusValue || asked || value.current === null) return;
    value.current.focus();
    setFocusValue(false);
  }, [focusValue, asked]);
  // A record's text spread over the form's lines reads as its own paragraph, full width (frame 34).
  const paragraph = editing ? undefined : recordParagraph(item);
  // The fee's field carries the chart under it, so the item stacks as frame 34 draws it.
  const charted = feeChart !== undefined && editable && (asked || troubled || editing);
  const twoColumn =
    item.state !== 'records_differ' &&
    paragraph === undefined &&
    !charted &&
    !(editable && (asked || troubled || editing) && !isSimpleAsk(item));

  let right: ReactNode;
  if (item.state === 'statement') {
    right = (
      <SourceLine testId={`${PREFIX}-${item.key}-printed`}>
        {item.key === 'attached_statement'
          ? `${PRINTED_ON_THE_FORM} ${PRINTED_ATTACHMENTS_CLAUSE}`
          : PRINTED_ON_THE_FORM}
      </SourceLine>
    );
  } else if (item.state === 'records_differ') {
    right = (
      <RecordsDiffer
        item={item}
        jurisdiction={jurisdiction}
        editable={editable}
        context={context}
        pending={answer.isPending}
        onUse={(source) =>
          send({
            kind: 'choose_source',
            value: { source: source.kind, value_sha256: source.valueSha256 ?? '' },
          })
        }
      />
    );
  } else if (editable && (asked || troubled || editing)) {
    right = (
      <AskItem
        item={item}
        jurisdiction={jurisdiction}
        initial={editing || troubled ? item.values : []}
        pending={answer.isPending}
        onAnswer={send}
        {...(feeChart !== undefined ? { feeChart } : {})}
        onCancel={editing ? () => setEditing(false) : undefined}
        sameAsLabel={sameAsLabel}
        fiscalYearEnd={context.fiscalYearEnd}
        onFiscalYearEndSaved={() => setFocusValue(true)}
      />
    );
  } else if (paragraph !== undefined && item.sources[0] !== undefined) {
    // Frame 34's purpose: the mission, its count, the form's guidance on length, and the form's
    // own way out of it ("Write “see attached”") beside Change.
    right = (
      <div className="flex min-w-0 flex-col gap-4">
        <p
          className="line-clamp-3 text-s14 leading-h15 text-text"
          {...testIdProp(`${PREFIX}-${item.key}-value`)}
        >
          {paragraph}
        </p>
        <SourceLine testId={`${PREFIX}-${item.key}-source`}>
          {`${sourceLine(item.sources[0], context)} · ${wordCountLine(paragraph)}`}
        </SourceLine>
        <Instruction item={item} jurisdiction={jurisdiction} />
        {editable && (
          <div className="flex flex-wrap gap-x-16 gap-y-4">
            <QuietButton onClick={() => setEditing(true)} testId={`${PREFIX}-${item.key}-change`}>
              {CHANGE}
            </QuietButton>
            {item.routes.map((route) => (
              <QuietButton
                key={route.key}
                onClick={() => send({ kind: 'route', value: route.key })}
                testId={`${PREFIX}-${item.key}-route-${route.key}`}
              >
                {`Write “${route.writes}”`}
              </QuietButton>
            ))}
          </div>
        )}
      </div>
    );
  } else if (asked) {
    right = (
      <div className="flex flex-col gap-4">
        <p
          className="text-s14 text-text-tertiary"
          {...testIdProp(`${PREFIX}-${item.key}-unanswered`)}
        >
          {NOT_ANSWERED_YET}
        </p>
        {item.state === 'optional_empty' && (
          <SourceLine>{notRequiredLine(jurisdiction)}</SourceLine>
        )}
      </div>
    );
  } else {
    const changed = changedFromProfile(item);
    const byLine =
      item.answer !== undefined
        ? changed !== undefined
          ? `${answeredLine(item, item.answer, personName, sameAsLabel).replace(/^Entered /, 'Changed ')}. Your profile still says ${changed.value}.`
          : answeredLine(item, item.answer, personName, sameAsLabel)
        : item.sources[0] !== undefined
          ? sourceLine(item.sources[0], context)
          : undefined;
    const changeable = editable && !isFiscalYearEndItem(item) && item.kind !== 'unknown';
    right = (
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-baseline justify-between gap-12">
          <div
            ref={value}
            // Only the fiscal year end takes focus here, once it is saved (above).
            tabIndex={isFiscalYearEndItem(item) ? -1 : undefined}
            className="min-w-0 text-s14 leading-h15 text-text outline-none"
            {...testIdProp(`${PREFIX}-${item.key}-value`)}
          >
            {lines.length > 0 ? lines.map((line, index) => <p key={index}>{line}</p>) : null}
          </div>
          {changeable && (
            <QuietButton onClick={() => setEditing(true)} testId={`${PREFIX}-${item.key}-change`}>
              {CHANGE}
            </QuietButton>
          )}
        </div>
        {byLine !== undefined && (
          <SourceLine testId={`${PREFIX}-${item.key}-source`}>{byLine}</SourceLine>
        )}
        {troubled && item.problem !== undefined && <SourceLine>{item.problem}</SourceLine>}
      </div>
    );
  }

  return (
    <li
      className={cn(
        'px-20 py-12',
        twoColumn
          ? 'grid grid-cols-1 gap-x-20 gap-y-4 sm:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]'
          : 'flex flex-col gap-6',
        selected && 'bg-brand-50/60',
      )}
      onClick={onSelect}
      onFocusCapture={onSelect}
      {...testIdProp(`${PREFIX}-${item.key}`)}
    >
      {label}
      {right}
      {troubled && editable && item.problem !== undefined && !editing && (
        <SourceLine testId={`${PREFIX}-${item.key}-problem`}>{item.problem}</SourceLine>
      )}
      {refusal !== null && (
        <p
          className="text-s12 leading-h15 text-text"
          role="alert"
          {...testIdProp(`${PREFIX}-${item.key}-refusal`)}
        >
          {refusal}
        </p>
      )}
    </li>
  );
}

/** An item asked in the two columns: one line, an amount, a month, or the form's Yes and No. */
function isSimpleAsk(item: FormItem): boolean {
  if (isFiscalYearEndItem(item)) return false;
  if (isYesNo(item)) return item.label.length < 140;
  return item.kind === 'text' || item.kind === 'amount' || item.kind === 'month';
}

function RecordsDiffer({
  item,
  jurisdiction,
  editable,
  context,
  pending,
  onUse,
}: {
  item: FormItem;
  jurisdiction: string;
  editable: boolean;
  context: SourceContext;
  pending: boolean;
  onUse: (source: FormSource) => void;
}) {
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const chosen = item.candidates.find((candidate) => candidate.kind === picked);
  return (
    <div className="flex flex-col gap-6">
      <p className="text-s13 text-text">{RECORDS_DIFFER}</p>
      <RadioTileGroup
        className="grid grid-cols-1 gap-8 sm:grid-cols-2"
        value={picked}
        onValueChange={setPicked}
        disabled={!editable}
        aria-label={itemDisplayLabel(item)}
        testId={`${PREFIX}-${item.key}-candidates`}
      >
        {item.candidates.map((candidate) => (
          <RadioTile
            key={candidate.kind}
            value={candidate.kind}
            label={candidate.value ?? ''}
            description={sourceLine(candidate, context)}
            testId={`${PREFIX}-${item.key}-candidate-${candidate.kind}`}
          />
        ))}
      </RadioTileGroup>
      <Instruction item={item} jurisdiction={jurisdiction} />
      {editable && (
        <div className="pt-2">
          <Button
            appearance="saas"
            tone="primary"
            size="sm"
            disabled={chosen === undefined || chosen.valueSha256 === undefined}
            loading={pending}
            onClick={() => {
              if (chosen !== undefined) onUse(chosen);
            }}
            testId={`${PREFIX}-${item.key}-use`}
          >
            {USE_THIS}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The form's own two answers, as one choice: a click answers. The answer chosen is drawn pressed in
 * the design system's pressed style (`FilterChip`'s: brand border, tint and text), since a group stays
 * asked once its Yes asks the firms table; the hover tint alone read as no choice at all. Each half
 * draws its own border, the pair's middle one shared, so the chosen half's border shows whole. The
 * rest is frame 34's pair as drawn: a black outline and a secondary-text divider (named here; they
 * were the text colours the borders fell back to under a border class that named no theme token).
 */
export function YesNo({
  item,
  pending,
  onAnswer,
}: {
  item: FormItem;
  pending: boolean;
  onAnswer: (body: ComplianceFormAnswerIn) => void;
}) {
  const ordered = [...item.choices].sort((a, b) =>
    a.label.trim().toLowerCase() === 'yes' ? -1 : b.label.trim().toLowerCase() === 'yes' ? 1 : 0,
  );
  return (
    <div className="inline-flex w-fit" role="group" aria-label={itemDisplayLabel(item)}>
      {ordered.map((choice, index) => {
        const chosen = item.choice === choice.key;
        return (
          <button
            key={choice.key}
            type="button"
            disabled={pending}
            aria-pressed={chosen}
            onClick={() => onAnswer({ kind: 'value', value: choice.key })}
            className={cn(
              'relative border px-14 py-6 text-s13 font-w500 focus-visible:z-20',
              index === 0 ? 'rounded-l-md' : '-ml-px',
              index === ordered.length - 1 && 'rounded-r-md',
              chosen
                ? 'z-10 border-brand-500 bg-brand-50 text-brand-700 hover:bg-brand-100'
                : 'border-black text-text-secondary hover:bg-surface-sunken hover:text-text',
              !chosen && index > 0 && 'border-l-text-secondary',
            )}
            {...testIdProp(`${PREFIX}-${item.key}-${choice.key}`)}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}

/** Whether the form's "Same as" is offered: it names the item it copies, the mailing address. */
function offersSameAs(item: FormItem, sameAsLabel: string | undefined): boolean {
  return (
    item.sameAs !== undefined && sameAsLabel !== undefined && /mailing address/i.test(sameAsLabel)
  );
}

/** Whether the form offers any quiet answer beside the item: "Doesn't apply", a route, "Same as". */
export function offersOtherAnswers(item: FormItem, sameAsLabel?: string): boolean {
  return (
    item.doesntApply !== undefined || item.routes.length > 0 || offersSameAs(item, sameAsLabel)
  );
}

/** The quiet answers the form offers beside a field: "Doesn't apply", its routes, "Same as". */
export function OtherAnswers({
  item,
  sameAsLabel,
  onAnswer,
}: {
  item: FormItem;
  sameAsLabel?: string;
  onAnswer: (body: ComplianceFormAnswerIn) => void;
}) {
  const sameAs = offersSameAs(item, sameAsLabel);
  return (
    <>
      {sameAs && (
        <QuietButton
          onClick={() => onAnswer({ kind: 'same_as' })}
          testId={`${PREFIX}-${item.key}-same-as`}
        >
          Same as the mailing address
        </QuietButton>
      )}
      {item.routes.map((route) => (
        <QuietButton
          key={route.key}
          onClick={() => onAnswer({ kind: 'route', value: route.key })}
          testId={`${PREFIX}-${item.key}-route-${route.key}`}
        >
          {`Write “${route.writes}”`}
        </QuietButton>
      ))}
      {item.doesntApply !== undefined && (
        <QuietButton
          onClick={() => onAnswer({ kind: 'doesnt_apply' })}
          testId={`${PREFIX}-${item.key}-doesnt-apply`}
        >
          {DOESNT_APPLY}
        </QuietButton>
      )}
    </>
  );
}

/** An item asked in place: the field(s) its kind takes, Save, and the form's other answers. */
export function AskItem({
  item,
  jurisdiction,
  initial,
  pending,
  onAnswer,
  onCancel,
  sameAsLabel,
  fiscalYearEnd,
  feeChart,
  instruction = true,
  onFiscalYearEndSaved,
}: {
  item: FormItem;
  jurisdiction: string;
  initial: readonly string[];
  pending: boolean;
  onAnswer: (body: ComplianceFormAnswerIn) => void;
  onCancel?: () => void;
  sameAsLabel?: string;
  fiscalYearEnd?: string;
  /** The form's fee chart, under the field in place of the instruction that points to it (Q3). */
  feeChart?: FormFeeChart;
  /** Whether the item's own instruction is quoted under it (a group quotes its items' once). */
  instruction?: boolean;
  /** The organization's fiscal year end saved here, once the reads hold it. */
  onFiscalYearEndSaved?: () => void;
}) {
  const slots =
    item.kind === 'lines' || item.kind === 'table' ? Math.max(1, item.places.length) : 1;
  const [texts, setTexts] = useState<string[]>(() =>
    Array.from({ length: slots }, (_, index) => initial[index] ?? ''),
  );
  const [month, setMonth] = useState<string>('');
  const set = (index: number, value: string) =>
    setTexts((current) => current.map((text, at) => (at === index ? value : text)));

  // The fiscal year end is the organization's, not this form's: it is saved to the profile, asked in
  // place as frame 34 asks it (no card, no Cancel; CCM-R1-INTAKE-NODATE-FYE).
  if (isFiscalYearEndItem(item)) {
    return (
      <div className="flex flex-col gap-6">
        <WorkbenchFiscalYearEnd
          {...(fiscalYearEnd !== undefined ? { current: fiscalYearEnd } : {})}
          testId={`${PREFIX}-fiscal-year-end`}
          {...(onFiscalYearEndSaved !== undefined ? { onSaved: onFiscalYearEndSaved } : {})}
        />
        <Instruction item={item} jurisdiction={jurisdiction} />
      </div>
    );
  }

  const footer = (save: ReactNode) => (
    <div className="flex flex-wrap items-center gap-x-16 gap-y-6 pt-2">
      {save}
      <OtherAnswers item={item} sameAsLabel={sameAsLabel} onAnswer={onAnswer} />
      {onCancel !== undefined && (
        <QuietButton onClick={onCancel} testId={`${PREFIX}-${item.key}-cancel`}>
          {CANCEL}
        </QuietButton>
      )}
    </div>
  );
  const saveButton = (disabled: boolean, onSave: () => void) => (
    <Button
      appearance="saas"
      tone="primary"
      size="sm"
      disabled={disabled}
      loading={pending}
      onClick={onSave}
      testId={`${PREFIX}-${item.key}-save`}
    >
      {SAVE}
    </Button>
  );

  let control: ReactNode;
  if (isYesNo(item)) {
    control = (
      <>
        <YesNo item={item} pending={pending} onAnswer={onAnswer} />
        {footer(null)}
      </>
    );
  } else if (item.kind === 'checked') {
    control = (
      <>
        <RadioTileGroup
          className="flex flex-col gap-6"
          value={item.choice}
          onValueChange={(key) => onAnswer({ kind: 'value', value: key })}
          disabled={pending}
          aria-label={itemDisplayLabel(item)}
          testId={`${PREFIX}-${item.key}-choices`}
        >
          {item.choices.map((choice) => (
            <RadioTile key={choice.key} value={choice.key} label={choice.label} />
          ))}
        </RadioTileGroup>
        {footer(null)}
      </>
    );
  } else if (item.kind === 'month') {
    control = (
      <div className="flex flex-wrap items-center gap-8">
        <Select
          appearance="saas"
          aria-label={itemDisplayLabel(item)}
          className="w-[200px]"
          value={month === '' ? null : month}
          onValueChange={(next) => setMonth(next ?? '')}
          options={MONTHS.map((name, index) => ({ value: String(index + 1), label: name }))}
        />
        {footer(saveButton(month === '', () => onAnswer({ kind: 'value', value: Number(month) })))}
      </div>
    );
  } else if (item.kind === 'lines' || item.kind === 'table') {
    const columns = item.kind === 'table' ? tableColumns(item) : 1;
    const rows: string[][] = [];
    for (let index = 0; index < texts.length; index += columns) {
      rows.push(texts.slice(index, index + columns));
    }
    const value = item.kind === 'table' ? rows : texts;
    const empty = texts.every((text) => text.trim().length === 0);
    control = (
      <>
        <div className="flex flex-col gap-6">
          {rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={cn(
                'grid gap-8',
                columns > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
              )}
            >
              {row.map((text, columnIndex) => {
                const at = rowIndex * columns + columnIndex;
                return (
                  <Field
                    key={columnIndex}
                    aria-label={`${itemDisplayLabel(item)} ${rowIndex + 1}`}
                    placeholder={
                      /names? and addresses/i.test(item.label) && columns === 1
                        ? NAME_AND_ADDRESS
                        : undefined
                    }
                    value={text}
                    onChange={(event) => set(at, event.target.value)}
                    testId={`${PREFIX}-${item.key}-line-${at + 1}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
        {footer(saveButton(empty, () => onAnswer({ kind: 'value', value })))}
      </>
    );
  } else {
    const amount = item.kind === 'amount';
    const text = texts[0] ?? '';
    // A field keeps the amount's 140px beside its actions; where they would leave it less, they go
    // under it (item 5's "Same as the mailing address" left its field 3px wide at 390).
    control = (
      <div className="flex flex-wrap items-center gap-8">
        <Field
          aria-label={itemDisplayLabel(item)}
          containerClassName={cn('min-w-0', amount ? 'w-[140px]' : 'flex-1 basis-[140px]')}
          leading={amount ? <span className="text-s14 text-text-tertiary">$</span> : undefined}
          inputMode={amount ? 'decimal' : undefined}
          value={text}
          onChange={(event) => set(0, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && text.trim().length > 0) {
              event.preventDefault();
              onAnswer({ kind: 'value', value: text });
            }
          }}
          testId={`${PREFIX}-${item.key}-field`}
        />
        {footer(
          saveButton(text.trim().length === 0, () => onAnswer({ kind: 'value', value: text })),
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {control}
      {item.state === 'optional_empty' && <SourceLine>{notRequiredLine(jurisdiction)}</SourceLine>}
      {feeChart !== undefined ? (
        <FeeChart
          chart={feeChart}
          jurisdiction={jurisdiction}
          testId={`${PREFIX}-${item.key}-fee-chart`}
        />
      ) : (
        instruction && <Instruction item={item} jurisdiction={jurisdiction} />
      )}
    </div>
  );
}
